import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  Box,
  Chip,
  IconButton,
  Avatar,
  CircularProgress,
  Divider,
  TextField,
  Grid,
} from '@mui/material';
import {
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  EventBusy as LeaveIcon,
  Schedule as LateIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { green, red, orange, amber } from '@mui/material/colors';
import { isCheckInTimeLate, getAttendanceStatusForCheckIn } from '../../utils/attendanceValidation';

const ATTENDANCE_OPTIONS = [
  {
    value: 'Present',
    label: 'Present',
    icon: PresentIcon,
    color: green[500],
    bgColor: green[50],
  },
  {
    value: 'Absent',
    label: 'Absent',
    icon: AbsentIcon,
    color: red[500],
    bgColor: red[50],
  },
  {
    value: 'On Leave',
    label: 'On Leave',
    icon: LeaveIcon,
    color: orange[500],
    bgColor: orange[50],
  },
  {
    value: 'Late',
    label: 'Late',
    icon: LateIcon,
    color: amber[600],
    bgColor: amber[50],
  },
];

const BatchAttendanceDialog = ({
  open,
  onClose,
  onSubmit,
  employees,
  isLoading,
  selectedDate = new Date().toISOString().split('T')[0],
}) => {
  const [attendanceData, setAttendanceData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const calculateTotalHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '';

    try {
      const [checkInHour, checkInMin] = checkIn.split(':').map(Number);
      const [checkOutHour, checkOutMin] = checkOut.split(':').map(Number);

      const checkInMinutes = checkInHour * 60 + checkInMin;
      let checkOutMinutes = checkOutHour * 60 + checkOutMin;

      if (checkOutMinutes <= checkInMinutes) {
        checkOutMinutes += 24 * 60;
      }

      const totalMinutes = checkOutMinutes - checkInMinutes;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours}.${Math.round((minutes / 60) * 100)
        .toString()
        .padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (!open) {
      setAttendanceData({});
      return;
    }

    if (employees.length === 0) return;

    setAttendanceData((prev) => {
      const next = { ...prev };

      employees.forEach((employee) => {
        if (next[employee.id]) return;

        const defaultCheckIn = '19:00';
        const defaultCheckOut = '04:00';
        const totalHours = calculateTotalHours(defaultCheckIn, defaultCheckOut);
        const initialStatus = getAttendanceStatusForCheckIn(defaultCheckIn, 'Present');

        next[employee.id] = {
          status: initialStatus,
          checkInTime: defaultCheckIn,
          checkOutTime: defaultCheckOut,
          totalHours,
          remarks: '',
        };
      });

      return next;
    });
  }, [open, employees]);

  const handleStatusChange = (employeeId, status) => {
    setAttendanceData((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        status,
      },
    }));
  };

  const handleTimeChange = (employeeId, field, value) => {
    setAttendanceData((prev) => {
      const current = prev[employeeId];
      const updated = { ...current, [field]: value };

      if (field === 'checkInTime') {
        const currentStatus = current.status;
        updated.status = getAttendanceStatusForCheckIn(value, currentStatus);
      }

      if (field === 'checkInTime' || field === 'checkOutTime') {
        updated.totalHours = calculateTotalHours(
          field === 'checkInTime' ? value : current.checkInTime,
          field === 'checkOutTime' ? value : current.checkOutTime
        );
      }

      return {
        ...prev,
        [employeeId]: updated,
      };
    });
  };

  const handleRemarksChange = (employeeId, value) => {
    setAttendanceData((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        remarks: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const attendanceRecords = Object.entries(attendanceData).map(([employeeId, data]) => ({
        employee_id: parseInt(employeeId, 10),
        date: selectedDate,
        status: data.status,
        check_in_time: data.status === 'Present' || data.status === 'Late' ? data.checkInTime : null,
        check_out_time: data.status === 'Present' || data.status === 'Late' ? data.checkOutTime : null,
        total_hours: data.status === 'Present' || data.status === 'Late' ? parseFloat(data.totalHours) || null : null,
        remarks: data.remarks || '',
      }));

      await onSubmit(attendanceRecords);
      onClose();
    } catch (error) {
      console.error('Error submitting batch attendance:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const getStatusCount = (status) => Object.values(attendanceData).filter((data) => data.status === status).length;

  const getSelectedDateString = () =>
    new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth disableEscapeKeyDown={submitting}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="primary" />
          <Typography variant="h6">Generate Batch Attendance</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {getSelectedDateString()}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading employees...
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Summary
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {ATTENDANCE_OPTIONS.map((option) => (
                  <Chip
                    key={option.value}
                    size="small"
                    label={`${option.label}: ${getStatusCount(option.value)}`}
                    icon={<option.icon />}
                    sx={{
                      backgroundColor: option.bgColor,
                      color: option.color,
                      '& .MuiChip-icon': { color: option.color },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Employees ({employees.length})
            </Typography>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {employees.map((employee) => {
                const employeeData = attendanceData[employee.id] || {};
                const selectedStatus = employeeData.status || 'Present';
                const showTimeFields = selectedStatus === 'Present' || selectedStatus === 'Late';

                return (
                  <ListItem
                    key={employee.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2,
                      backgroundColor: 'background.paper',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      p: 2,
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={showTimeFields ? 1 : 0}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {employee.full_name.charAt(0).toUpperCase()}
                      </Avatar>

                      <Box flexGrow={1}>
                        <Typography variant="subtitle2">{employee.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {employee.id} | {employee.designation || 'N/A'}
                        </Typography>
                      </Box>

                      <Box display="flex" gap={1}>
                        {ATTENDANCE_OPTIONS.map((option) => {
                          const isSelected = selectedStatus === option.value;
                          const IconComponent = option.icon;

                          return (
                            <IconButton
                              key={option.value}
                              onClick={() => handleStatusChange(employee.id, option.value)}
                              sx={{
                                backgroundColor: isSelected ? option.bgColor : 'transparent',
                                color: isSelected ? option.color : 'text.secondary',
                                border: isSelected ? `2px solid ${option.color}` : '2px solid transparent',
                                '&:hover': {
                                  backgroundColor: option.bgColor,
                                  color: option.color,
                                },
                              }}
                              title={option.label}
                            >
                              <IconComponent />
                            </IconButton>
                          );
                        })}
                      </Box>
                    </Box>

                    {showTimeFields && (
                      <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={3}>
                          <TextField
                            label="Check In"
                            type="time"
                            size="small"
                            value={employeeData.checkInTime || '19:00'}
                            onChange={(e) => handleTimeChange(employee.id, 'checkInTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            helperText={
                              isCheckInTimeLate(employeeData.checkInTime)
                                ? 'Will be marked as Late (after 7:15 PM)'
                                : employeeData.status === 'Late' && !isCheckInTimeLate(employeeData.checkInTime)
                                ? 'Will be marked as Present (at or before 7:15 PM)'
                                : ''
                            }
                            sx={{
                              '& .MuiFormHelperText-root': {
                                color:
                                  isCheckInTimeLate(employeeData.checkInTime) ||
                                  (employeeData.status === 'Late' && !isCheckInTimeLate(employeeData.checkInTime))
                                    ? 'warning.main'
                                    : 'text.secondary',
                                fontSize: '0.7rem',
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="Check Out"
                            type="time"
                            size="small"
                            value={employeeData.checkOutTime || '04:00'}
                            onChange={(e) => handleTimeChange(employee.id, 'checkOutTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="Total Hours"
                            size="small"
                            value={employeeData.totalHours || ''}
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            label="Remarks"
                            size="small"
                            value={employeeData.remarks || ''}
                            onChange={(e) => handleRemarksChange(employee.id, e.target.value)}
                            placeholder="Optional remarks..."
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    )}
                  </ListItem>
                );
              })}
            </List>

            {employees.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  No employees found. Please ensure employees are added to the system.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || employees.length === 0}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Creating...' : `Create Attendance (${employees.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BatchAttendanceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string.isRequired,
      employee_id: PropTypes.string,
      designation: PropTypes.string,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  selectedDate: PropTypes.string,
};

BatchAttendanceDialog.defaultProps = {
  isLoading: false,
  selectedDate: new Date().toISOString().split('T')[0],
};

export default BatchAttendanceDialog;
